import React from 'react';
import clsx from 'clsx';

export type { EditorSaveStatus as SaveStatus } from '~/components/document-editor/use-editor-document-persistence';
import type { EditorSaveStatus as SaveStatus } from '~/components/document-editor/use-editor-document-persistence';

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  className?: string;
  /**
   * The server's refusal of a save: a version that is no longer the latest, a run in progress or a
   * cut of most of the text. Reloading the page fixes none of them, so the refusal offers no Reload.
   */
  staleVersionMessage?: string | null;
  /** Offered with a refusal when the workspace holds a newer version than the one refused. */
  onOpenLatest?: () => void;
}

/**
 * Shared component for displaying save status (Saving... / Saved)
 * Used in both workspace and documents pages for HTML document editing
 */
export const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({
  status,
  className,
  staleVersionMessage,
  onOpenLatest
}) => {
  if (staleVersionMessage) {
    return (
      <span
        role="alert"
        className={clsx('text-xs font-medium text-red-600 dark:text-red-400', className)}
      >
        {staleVersionMessage}
        {onOpenLatest && (
          <>
            {' '}
            <button
              type="button"
              onClick={onOpenLatest}
              className="underline hover:text-red-700 dark:hover:text-red-300"
            >
              Open latest version
            </button>
          </>
        )}
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'text-xs font-medium inline-block',
        status === 'saving'
          ? 'text-neutral-600 dark:text-neutral-400'
          : status === 'saved'
            ? 'text-primary-600 dark:text-primary-400'
            : status === 'error'
              ? 'text-red-600 dark:text-red-400'
              : 'text-transparent',
        className
      )}
      role={status === 'error' ? 'alert' : undefined}
      style={{ minWidth: '60px' }}
    >
      {status === 'saving'
        ? 'Saving...'
        : status === 'saved'
          ? 'Saved'
          : status === 'error'
            ? 'Save failed. Try again.'
            : '\u00A0'}
    </span>
  );
};
